import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError, type ApiResponse } from '../api/client';
import { getMe, isMfaRequired, login as apiLogin, logout as apiLogout, type MeResponse } from '../api/auth';

// Server is always authoritative; client-side state is never trusted for
// permission decisions.
interface AuthState {
  // 'unknown-space' = the dev host carries a Space subdomain that doesn't
  // resolve to any Space in the tenant (API → 404 UNKNOWN_SPACE_SUBDOMAIN).
  status: 'loading' | 'anonymous' | 'authenticated' | 'unknown-space';
  me: MeResponse | null;
  // Heslo prošlo, chybí druhý faktor: 'verify' = zadat kód, 'enroll' = nastavit 2FA.
  mfa: { mode: 'verify' | 'enroll' } | null;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  completeMfa: () => Promise<void>;
  cancelMfa: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading', me: null, mfa: null });

  const refresh = useCallback(async () => {
    try {
      const me = await getMe();
      setState({ status: 'authenticated', me, mfa: null });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'UNKNOWN_SPACE_SUBDOMAIN') {
        // Dev-only: misconfigured Space subdomain. Surface it loudly instead
        // of dropping to the login screen, which would hide the real cause.
        setState({ status: 'unknown-space', me: null, mfa: null });
        return;
      }
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        setState((prev) => ({ status: 'anonymous', me: null, mfa: prev.mfa }));
        return;
      }
      // Unknown error - treat as anonymous but surface to console so we
      // notice during dev. Production telemetry can hook here later.
      // eslint-disable-next-line no-console
      console.error('[auth] refresh failed', e);
      setState({ status: 'anonymous', me: null, mfa: null });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await apiLogin(email, password);
      if (isMfaRequired(result)) {
        setState({ status: 'anonymous', me: null, mfa: { mode: result.mode } });
        return;
      }
      await refresh();
    },
    [refresh],
  );

  const completeMfa = useCallback(async () => {
    await refresh();
  }, [refresh]);

  const cancelMfa = useCallback(() => {
    setState({ status: 'anonymous', me: null, mfa: null });
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch {
      // Even on error we drop the client-side state - the user wanted
      // out and the worst case is a leftover server session that the
      // next access will expire.
    }
    setState({ status: 'anonymous', me: null, mfa: null });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, logout, refresh, completeMfa, cancelMfa }),
    [state, login, logout, refresh, completeMfa, cancelMfa],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export type { ApiResponse };
